import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Phone, Mail, MapPin, ArrowRight, CheckCircle, Users, TrendingUp, Clock, Lock } from 'lucide-react';
import Login from '@/components/Login';

const LandingPage = ({ onLoginSuccess }) => {
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  if (showAdminLogin) {
    return (
      <div className="relative min-h-screen">
        <button
          onClick={() => setShowAdminLogin(false)}
          className="absolute top-4 left-4 text-white bg-black/30 hover:bg-black/50 px-4 py-2 rounded-lg backdrop-blur-sm z-50"
        >
          ← Zurück zur Startseite
        </button>
        <Login onLogin={onLoginSuccess} />
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });

      if (response.ok) {
        alert('✅ Vielen Dank! Wir werden uns in Kürze bei Ihnen melden.');
        setContactForm({ name: '', email: '', phone: '', message: '' });
      } else {
        alert('❌ Fehler beim Senden. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      alert('❌ Fehler beim Senden. Bitte versuchen Sie es erneut.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/60 to-transparent backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2 rounded-lg">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">RYAN GmbH</h1>
                <p className="text-xs text-gray-300">Hamburg und Norddeutschland</p>
              </div>
            </div>
            
            {/* Hidden Admin Login - Top Right */}
            <button
              onClick={() => setShowAdminLogin(true)}
              className="text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10"
              title="Admin Login"
            >
              <Lock className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section - Werkslogistik Background (NO PEOPLE) */}
      <section 
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.75)), url(https://images.unsplash.com/photo-1645736315000-6f788915923b?q=80&w=2000)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="container mx-auto px-4 py-24">
          <div className="max-w-4xl mx-auto text-center">
            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              <span className="block">Ihr starker Partner mit über 20 Jahren Erfahrung in</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">
                Hamburg und Norddeutschland
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Professionelle Lösungen für Containerlogistik, Kontrakt- und Werksvertragslogistik
            </p>

            <p className="text-lg md:text-xl text-gray-300 mb-12">
              Mehr als 10.000 erfolgreich bearbeitete Container und 25+ qualifizierte Mitarbeiter stehen für Sie bereit
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a 
                href="https://ryan-logistik.de"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl transition-all flex items-center justify-center gap-2"
              >
                Besuchen Sie unsere Website
                <ArrowRight className="h-5 w-5" />
              </a>
              <a 
                href="https://ryan-logistik.de/kontakt"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 backdrop-blur-md hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold text-lg border-2 border-white/30 transition-all flex items-center justify-center gap-2"
              >
                Kontakt aufnehmen
              </a>
            </div>

            {/* CTA to Website */}
            <Card className="max-w-2xl mx-auto bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="pt-6 text-center">
                <h3 className="text-white font-semibold text-xl mb-4">
                  Für weitere Informationen besuchen Sie unsere Website
                </h3>
                <a 
                  href="https://ryan-logistik.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                >
                  www.ryan-logistik.de
                  <ArrowRight className="h-5 w-5" />
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-950 border-t border-slate-800">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400 mb-4">
            RYAN GmbH - Ihr Partner für Logistik in Hamburg und Norddeutschland
          </p>
          <div className="flex justify-center gap-6 text-sm">
            <a 
              href="https://ryan-logistik.de" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300"
            >
              www.ryan-logistik.de
            </a>
            <a 
              href="https://ryan-logistik.de/kontakt" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300"
            >
              Kontakt
            </a>
          </div>
          <p className="text-gray-500 text-xs mt-4">
            © 2025 Ryan GmbH. Alle Rechte vorbehalten.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
